/* eslint-disable */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import type { AppThunk } from '../store'
import { HTTP } from '../lib/axios'
import { sortListByString, formatNumber } from '../utils/common'
import { Contact } from '../types/contact'

export interface ContactState {
  contacts: Array<Contact>
  selectedContact: Contact
  loadingContact: boolean
  loading: boolean
  saving: boolean
  errors: string
  contactNumberMap: any
}
export const initialState: ContactState = {
  contacts: [],
  selectedContact: null,
  loadingContact: false,
  loading: false,
  saving: false,
  errors: '',
  contactNumberMap: {}
}
const contactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload
    },
    setLoadingContact: (state, { payload }: PayloadAction<boolean>) => {
      state.loadingContact = payload
    },
    setSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.saving = payload
    },
    setErrors: (state, { payload }: PayloadAction<string>) => {
      state.errors = payload
    },
    setContacts: (state, { payload }: PayloadAction<Contact[]>) => {
      const filtered = payload.filter((contact: any) => contact.lastName)
      state.contacts = sortListByString(filtered, 'lastName', true)
      const cmap: any = {}
      for (const contact of payload) {
        for (const number of contact.contactDetails.phoneNumbers) {
          cmap[formatNumber(number.number)] = { ...contact, number }
        }
      }
      state.contactNumberMap = cmap
    },
    setSelectedContact: (state, { payload }: PayloadAction<Contact>) => {
      state.selectedContact = payload
    },
    appendContacts: (state, { payload }: PayloadAction<any>) => {
      state.contacts = sortListByString(
        [state.contacts, ...payload],
        'lastName',
        true
      )
    },
    updateContact: (state, { payload }: PayloadAction<Contact>) => {
      const existing = state.contacts.findIndex(
        (c) => c.contactId === payload.contactId
      )
      if (existing > -1) {
        const clone = [...state.contacts]
        clone[existing] = { ...clone[existing], ...payload }
        state.contacts = clone
      } else {
        state.contacts = sortListByString(
          [...state.contacts, payload],
          'lastName',
          true
        )
      }
    }
  }
})

export const {
  setLoading,
  setLoadingContact,
  setSaving,
  setErrors,
  setContacts,
  setSelectedContact,
  appendContacts,
  updateContact
} = contactSlice.actions
export default contactSlice.reducer
export const contactSelector = (state: { contactStore: ContactState }) =>
  state.contactStore

const loadAllContacts = async (users = [], url: string, config) =>
  await HTTP.get(url, config).then(({ data }) => {
    if (data.Count < 1) return users

    const startKey = data.startKey.contactId
    if (startKey) {
      const params = new URLSearchParams()
      params.append('startKey', startKey)
      config.params = params
      return loadAllContacts(
        [...users, ...data.contacts],
        `${url}?startKey=${data.startKey.contactId}`,
        config
      )
    }
    users.push(...data.contacts)
    return users
  })

// AppThunk sets the type definitions for the dispatch method
export const getAllContacts = (token: string): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading(true))
    try {
      const url = `/contacts/allContacts`
      const config = {
        headers: {
          Authorization: 'Bearer ' + token
        }
      }
      const contacts = await loadAllContacts([], url, config)
      dispatch(setLoading(false))
      dispatch(setContacts(contacts))
    } catch (error) {
      console.error(error)
      dispatch(setLoading(false))
      dispatch(setContacts([]))
    }
  }
}

// account/{accountId}/user/{australisId}/contact/{contactId}
export const getContact = (
  token: string,
  accountId: string,
  australisId: string,
  contactId: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoadingContact(true))
    try {
      const res = await HTTP.get(
        `/contacts/account/${accountId}/user/${australisId}/contact/${contactId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token
          }
        }
      )
      dispatch(setSelectedContact(res.data.contact))
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setLoadingContact(false))
    }
  }
}

export const saveContactSpecificSettings = (
  token: string,
  accountId: string,
  australisId: string,
  contactId: string,
  settings: any
): AppThunk => {
  return async (dispatch, getState) => {
    const { contacts } = getState()
    const { selectedContact } = contacts
    dispatch(setSaving(true))
    try {
      console.log(settings)
      const res = await HTTP.post(
        `/contacts/account/${accountId}/user/${australisId}/contact/${contactId}`,
        { contactSettings: settings },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      const { data } = res.data
      dispatch(
        setSelectedContact({
          ...selectedContact,
          contactSettings: { ...selectedContact.contactSettings, ...settings }
        })
      )
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setSaving(false))
    }
  }
}

export const searchContacts = (
  token: string,
  searchString: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading(true))
    try {
      const res = await HTTP.get(`/${searchString}`, {
        headers: {
          Authorization: 'Bearer ' + token
        }
      })
      const { contacts } = res.data
      dispatch(setLoading(false))
      dispatch(setContacts(contacts))
    } catch (error) {
      console.error(error)
      dispatch(setLoading(false))
      dispatch(setContacts([]))
    }
  }
}
